import React, { useMemo, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import {
    DataGrid,
    Column,
    Lookup,
    Scrolling,
    LoadPanel,
    Editing,
    FilterRow,
    ColumnChooser,
    ColumnChooserSearch,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import notify from "devextreme/ui/notify";
import routes from "../../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../../common/GridLayout";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";
import { MESSAGES } from "../../../../../common/Messages";
import { createConcurrencyAwareStore, refreshGridOnConflict } from "../../../../../common/dataStore";
import {
    EMPTY_GUID,
    OrderStages,
    PrintInvoiceStatus,
    TransactionStatus,
    YesNo,
} from "../../../../../enum/Enum";

// OMD0026 Stage/Status pick lists. The codes come from the shared enums — OrderStages is already
// OMTransactionHeader.STAGE — and only the labels are OMD0026's own, taken from the Codes/Items pairs
// on qryOMTransactionsStage and qryOMTransactionsStatus in uOMD0026.dfm. The status list is
// deliberately narrower than its enum: only invoice-type transactions reach this grid.
const omStages = [
    { id: OrderStages.Canceled, text: "Canceled" },
    { id: OrderStages.Quote, text: "Quote" },
    { id: OrderStages.Plan, text: "Plan" },
    { id: OrderStages.Future, text: "Future" },
    { id: OrderStages.Pending, text: "Pending" },
    { id: OrderStages.Assigned, text: "Assigned" },
    { id: OrderStages.PickedStaged, text: "Picked/Staged" },
    { id: OrderStages.ShippedCompleted, text: "Shipped/Completed" },
];

const omStatuses = [
    { id: TransactionStatus.Open, text: "Open" },
    { id: TransactionStatus.Hold, text: "Hold" },
    { id: TransactionStatus.ReadyToCommitLater, text: "Ready To Commit Later" },
    { id: TransactionStatus.Committed, text: "Committed" },
];

// OMTRANSACTIONHEADER.PRINTINVOICE and IMTRANSACTIONLOCATION.AVAILABLEFLAG. Both used to arrive as a
// description built in SQL; they come over as their raw codes now and are described here, the same as
// Stage and Status (Architecture.md rule 3).
const invoicePrintedOptions = [
    { id: PrintInvoiceStatus.Yes, text: "Yes" },
    { id: PrintInvoiceStatus.No, text: "No" },
    { id: PrintInvoiceStatus.Printed, text: "Printed" },
];

const availableOptions = [
    { id: YesNo.Yes, text: "Yes" },
    { id: YesNo.No, text: "No" },
];

// EnableDisableControls: edtBackordered is enabled for stages A/S/C only, and only while the back
// order is non-zero. The server enforces the same rule — this only keeps the cell from opening.
const backOrderEditableStages = [
    OrderStages.Assigned,
    OrderStages.PickedStaged,
    OrderStages.ShippedCompleted,
];

// RunScript writes IMTRANSACTIONLOCATION only under `if @Stage in ('F','N','A','S','C')`. Delphi still
// lets the quantity be typed at other stages and fails on save; the cell is closed here instead, and
// the server rejects it either way.
const quantityEditableStages = [
    OrderStages.Future,
    OrderStages.Pending,
    ...backOrderEditableStages,
];

function OpenTransactionsForItemLotPopup(props) {
    const { showPopup = {}, setShowPopup, oidc } = props;
    const visible = showPopup.visible === true;
    const itemId = showPopup.itemId;
    const lotId = showPopup.lotId;
    const accessToken = oidc?.user?.access_token;

    const dataGridRef = useRef(null);
    const gridKey = "CD310C4E-651B-4EE0-9CC6-3388BCC76A64";
    const [currentLayout, setCurrentLayout] = useState("Default");
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [currentLayout, accessToken]);
    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [gridKey, currentLayout, accessToken]);

    const dataSource = useMemo(() => {
        if (!itemId || !accessToken) return null;

        const lotParam = lotId && lotId !== EMPTY_GUID ? lotId : undefined;

        return createConcurrencyAwareStore({
            key: "Rowid",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetTransactionDetailsForItemLot}`,
            updateUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.UpdateTransactionDetailsForItemLot}`,
            loadParams: {
                itemId,
                ...(lotParam ? { lotId: lotParam } : {}),
            },
            // A stale edit refreshes this one grid rather than discarding the page (Architecture.md
            // rule 16). Nothing outside the popup derives from these rows, so there is nothing to
            // pass as a derived refresher.
            onConflict: refreshGridOnConflict(dataGridRef),
            onModified() {
                notify({ message: MESSAGES.OPEN_TRANSACTIONS_CHANGE_SAVED, width: 260 }, "success", 2500);
            },
        }, accessToken);
    }, [itemId, lotId, accessToken]);

    const handleHiding = useCallback(() => {
        // FormClose: "Please SAVE or CANCEL Before Proceed" — Delphi refuses to close over an
        // uncommitted edit rather than silently dropping it.
        const grid = dataGridRef.current?.instance();
        if (grid?.hasEditData()) {
            notify({ message: MESSAGES.SAVE_OR_CANCEL_BEFORE_CLOSE, width: 320 }, "error", 3000);
            return;
        }
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    // Cell editing posts only the changed cell, so merge the old row in first: that merge is what
    // carries ConcurrencyToken into the PUT (Architecture.md rules 15 and 16). It also lets the
    // server tell a quantity edit from a back-order edit by comparing against the stored values.
    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
    }, []);

    const onEditorPreparing = useCallback((e) => {
        if (e.parentType !== "dataRow") return;
        const row = e.row?.data;

        if (e.dataField === "Quantity") {
            e.editorOptions.disabled = !row || !quantityEditableStages.includes(row.Stage);
            return;
        }

        if (e.dataField === "OutBackorder") {
            e.editorOptions.disabled =
                !row || !backOrderEditableStages.includes(row.Stage) || !row.OutBackorder;
        }
    }, []);

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? "Open Transactions for Item/Lot"}
            container="#root"
            width="90vw"
            height="90vh"
        >
            {!dataSource ? (
                <div style={{ padding: 16 }}>{MESSAGES.OPEN_TRANSACTIONS_SELECT_ITEM_LINE}</div>
            ) : (
                <DataGrid
                    key={gridKey}
                    ref={dataGridRef}
                    dataSource={dataSource}
                    columnAutoWidth={true}
                    allowColumnResizing={true}
                    allowColumnReordering={true}
                    hoverStateEnabled={true}
                    height="calc(100vh - 175px)"
                    showColumnLines={true}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    paging={false}
                    onRowUpdating={onRowUpdating}
                    onEditorPreparing={onEditorPreparing}
                >
                    <Scrolling mode="standard" />
                    <LoadPanel enabled={true} />
                    <Editing mode="cell" allowUpdating={true} useIcons={true} refreshMode="reshape" />
                    {/* abFind — Delphi exposed Find over Customer + Transaction# (navOMTransactions
                        CriteriaFields); the filter row covers it per column. */}
                    <FilterRow visible={true} />
                    <ColumnChooser
                        enabled={true}
                        title="Choose Column"
                        width={290}
                        height={290}
                        sortOrder="asc"
                    >
                        <ColumnChooserSearch
                            enabled={true}
                            editorOptions={{ placeholder: "Search column" }}
                        />
                    </ColumnChooser>
                    <Column dataField="RecordNumber" caption="Record #" allowEditing={false} />
                    <Column dataField="Lastupdatedatetime" visible={false} allowEditing={false} />
                    <Column dataField="RLocation" visible={false} allowEditing={false} />
                    <Column dataField="locationId" visible={false} allowEditing={false} />
                    <Column dataField="IdentityID" caption="Customer" allowEditing={false} />
                    <Column dataField="CustomerName" caption="Customer Name" visible={false} allowEditing={false} />
                    <Column dataField="Reference_1_Location" caption="Transaction#" allowEditing={false} />
                    <Column dataField="Stage" caption="Stage" allowEditing={false}>
                        <Lookup dataSource={omStages} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="Status" caption="Status" allowEditing={false}>
                        <Lookup dataSource={omStatuses} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="Source" caption="Invoice Printed" allowEditing={false}>
                        <Lookup dataSource={invoicePrintedOptions} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column
                        dataField="Quantity"
                        caption="Quantity"
                        dataType="number"
                        allowEditing={true}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    {/* edtBackordered — IMTRANSACTIONLOCATION.OUT_BACKORDER: the back order this screen
                        edits, and the one a quantity reduction is absorbed into. */}
                    <Column
                        dataField="OutBackorder"
                        caption="Back Ordered"
                        dataType="number"
                        allowEditing={true}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    {/* DetailBackOrdered — OMTRANSACTIONDETAIL.QUANTITYBACKORDER, read-only in Delphi too. */}
                    <Column
                        dataField="Quantity_1_Location"
                        caption="Detail Back Ordered"
                        dataType="number"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Grossweight"
                        caption="Amount"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column dataField="Availableflag" caption="Available" visible={false} allowEditing={false}>
                        <Lookup dataSource={availableOptions} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="Creationdatetime" caption="Created" visible={false} allowEditing={false} dataType="datetime" />
                    <Column dataField="CreationDateTime" caption="Detail Created" visible={false} allowEditing={false} dataType="datetime" />
                    <Column dataField="Creationuserid" caption="Created By" visible={false} allowEditing={false} />
                    <Column dataField="Lastupdateuserid" caption="Last Updated By" visible={false} allowEditing={false} />
                    <Column dataField="locationcode" caption="Location" visible={false} allowEditing={false} />
                    <Column dataField="Module" caption="Module" visible={false} allowEditing={false} />
                    <Column dataField="Comment" caption="Comment" visible={false} allowEditing={false} />
                    <Column
                        dataField="OnHand"
                        caption="Ordered"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    {/* DetailShipped — OMTRANSACTIONDETAIL.QUANTITYSHIPPED. Distinct from OutShipped
                        below, which is the location row's OUT_SHIPPED. */}
                    <Column
                        dataField="DetailShipped"
                        caption="Shipped"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Netweight"
                        caption="Original Amount"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="ItemAvailable"
                        caption="Original Ordered"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column
                        dataField="Originalquantity"
                        caption="Original Quantity"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "fixedPoint", precision: 2 }}
                    />
                    <Column dataField="Availablereasoncode" caption="Reason" visible={false} allowEditing={false} />
                    <Column dataField="Date_1_Location" caption="Ship Date" visible={false} allowEditing={false} dataType="datetime" />
                    <Column dataField="Transactiondate" caption="Transaction Date" visible={false} allowEditing={false} dataType="datetime" />
                    <Column
                        dataField="ForSale"
                        caption="Unit Price"
                        visible={false}
                        allowEditing={false}
                        format={{ type: "currency", precision: 4, currency: "USD" }}
                    />
                    <Column dataField="Recordtype" visible={false} allowEditing={false} />
                    <Column dataField="Sourcetablename" visible={false} allowEditing={false} />
                    <Column dataField="Quantityinventory" visible={false} allowEditing={false} />
                    <Column dataField="Quantityinventoryuom" visible={false} allowEditing={false} />
                    <Column dataField="Code_1_Location" caption="Backorder Flag" visible={false} allowEditing={false} />
                    <Column dataField="InOrdered" visible={false} allowEditing={false} />
                    <Column dataField="InReceiving" visible={false} allowEditing={false} />
                    <Column dataField="InBackorder" visible={false} allowEditing={false} />
                    <Column dataField="OutOrdered" visible={false} allowEditing={false} />
                    <Column dataField="OutShipped" visible={false} allowEditing={false} />
                    <Column dataField="InUnavailable" visible={false} allowEditing={false} />
                    <Column dataField="OutUnavailable" visible={false} allowEditing={false} />
                    <Column dataField="InFuture" visible={false} allowEditing={false} />
                    <Column dataField="OutFuture" visible={false} allowEditing={false} />
                    <GridToolbar>
                        <GridToolbarItem location="after">
                            <GridLayoutManager
                                gridKey={gridKey}
                                dataGrid={dataGridRef}
                                setCurrentLayout={setCurrentLayout}
                            />
                        </GridToolbarItem>
                        <GridToolbarItem name="exportButton" />
                        <GridToolbarItem name="columnChooserButton" />
                    </GridToolbar>
                </DataGrid>
            )}
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(OpenTransactionsForItemLotPopup);
